import React, { useState, useEffect, useMemo } from 'react';
import { View, LogBox, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import CustomDT from '../common/controls/customDataTable';
import { Loader } from '../../Common/Loader';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function SpoilLog(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const [spoilLogData, setSpoilLogData] = useState<null | any>(null);
  const [spoilLogColumns, setSpoilLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setSpoilLogData(json.items);
          setSpoilLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return spoilLogData && spoilLogColumns
    ? <CreateLogCharts spoilLogData={spoilLogData} spoilLogColumns={spoilLogColumns} siteId={props.siteId} logTitle={props.logTitle} clientAPIURL={clientAPIURL} />
    : <Loader />;
}

interface CreateLogChartsProps {
  spoilLogData: any;
  spoilLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({ spoilLogData, spoilLogColumns, siteId, logTitle, clientAPIURL }) => {
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = spoilLogColumns && [
    ...spoilLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(spoilLogData ?? []);

  useEffect(() => {
    setFilteredData(spoilLogData ?? []);
  }, [spoilLogData]);

  function returnDate(value: Date) {
    if (value) {
      const date = new Date(value);
      const timezoneOffset = 4 * 60;
      const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
      const day = adjustedDate.getDate();
      const month = adjustedDate.getMonth() + 1;
      const year = adjustedDate.getFullYear();
      return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
    } else {
      return value;
    }
  }

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const updatedDate = result.items[0] && returnDate(result.items[0].Date);
        const newItem = { ...result.items[0], Date: updatedDate };
        setFilteredData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  const chartData = useMemo(() => {
    const colorScale = scaleOrdinal(schemeCategory10);

    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))].filter(Boolean) as string[];

    // Items with valid QuantityTonne — for line charts only
    const qtyFiltered = filteredData.filter((item: any) =>
      item.QuantityTonne != null && item.QuantityTonne !== ''
    );

    // --- Pie 1: SpoilType / QuantityTonne ---
    const pieAccum1: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.SpoilType == null) return;
      const key = String(item.SpoilType);
      pieAccum1[key] = (pieAccum1[key] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const bySpoilCategoryPieData = Object.entries(pieAccum1).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType5(index.toString()),
    }));

    // --- Pie 2: SpoilDisposalMethod / QuantityTonne ---
    const pieAccum2: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.SpoilDisposalMethod == null) return;
      const key = String(item.SpoilDisposalMethod);
      pieAccum2[key] = (pieAccum2[key] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const bySpoilDisposalMethodPieData = Object.entries(pieAccum2).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType3(index.toString()),
    }));

    // --- Line 1: SpoilType monthly QuantityTonne ---
    const uniqueSpoilTypes = [...new Set(qtyFiltered.map((item: any) => item.SpoilType))].filter(Boolean) as string[];
    const periodTypeSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.SpoilType == null) return;
      const p = String(item.Period);
      const t = String(item.SpoilType);
      if (!periodTypeSums[p]) periodTypeSums[p] = {};
      periodTypeSums[p][t] = (periodTypeSums[p][t] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDataByType = {
      labels: uniquePeriod,
      datasets: uniqueSpoilTypes.map((type, index) => ({
        data: uniquePeriod.map(p => Math.round(periodTypeSums[p]?.[type] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(index)),
      })),
      legend: uniqueSpoilTypes.map(type => ({ name: type })),
    };

    // --- Line 2: SpoilDisposalMethod monthly QuantityTonne ---
    const uniqueDisposalMethods = [...new Set(qtyFiltered.map((item: any) => item.SpoilDisposalMethod))].filter(Boolean) as string[];
    const periodMethodSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.SpoilDisposalMethod == null) return;
      const p = String(item.Period);
      const m = String(item.SpoilDisposalMethod);
      if (!periodMethodSums[p]) periodMethodSums[p] = {};
      periodMethodSums[p][m] = (periodMethodSums[p][m] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineDataByDisposal = {
      labels: uniquePeriod,
      datasets: uniqueDisposalMethods.map((method, index) => ({
        data: uniquePeriod.map(p => Math.round(periodMethodSums[p]?.[method] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(uniqueSpoilTypes.length + index)),
      })),
      legend: uniqueDisposalMethods.map(method => ({ name: method })),
    };

    // --- Table 1: period × SpoilType ---
    const spoilTypeLabels = spoilLogColumns?.find((col: any) => col.columnName === 'SpoilType')?.choiceChoices;
    const spoilTypeChoices: string[] = spoilTypeLabels
      ? spoilTypeLabels.split('#;#').filter((v: string) => v !== 'undefined' && v !== 'null' && v !== '')
      : [...uniqueSpoilTypes];

    const table1Rows: Record<string, any> = {};
    filteredData.forEach((item: any) => {
      const { Period, SpoilType, QuantityTonne } = item;
      if (!Period) return;
      if (!table1Rows[Period]) {
        table1Rows[Period] = { Period };
        spoilTypeChoices.forEach((t: string) => { table1Rows[Period][t] = 0; });
      }
      if (SpoilType != null) {
        const t = String(SpoilType);
        table1Rows[Period][t] = (table1Rows[Period][t] ?? 0) + Math.round(Number(QuantityTonne) || 0);
      }
    });

    const mergedData1 = uniquePeriod.map(Period => {
      const row = table1Rows[Period] ?? { Period };
      const periodItems = filteredData.filter((item: any) => item.Period === Period);
      const sumQty = Math.round(periodItems.reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0));
      const typeTotal = spoilTypeChoices.reduce((s: number, t: string) => s + (row[t] ?? 0), 0);
      return {
        Period,
        'Number Of Samples': periodItems.length,
        Total: sumQty,
        ...spoilTypeChoices.reduce((acc: any, t: string) => { acc[t] = row[t] ?? 0; return acc; }, {}),
        'Total (By Disposal Method)': typeTotal,
      };
    });
    const columnKeys1 = mergedData1.length > 0
      ? Object.keys(mergedData1[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // --- Table 2: period × SpoilDisposalMethod + Reused summaries ---
    const spoilMethodLabels = spoilLogColumns?.find((col: any) => col.columnName === 'SpoilDisposalMethod')?.choiceChoices;
    const spoilDisposalChoices: string[] = spoilMethodLabels
      ? spoilMethodLabels.split('#;#').filter((v: string) => v !== 'undefined' && v !== 'null' && v !== '')
      : [...uniqueDisposalMethods];

    const table2Rows: Record<string, any> = {};
    filteredData.forEach((item: any) => {
      const { Period, SpoilDisposalMethod, QuantityTonne } = item;
      if (!Period) return;
      if (!table2Rows[Period]) {
        table2Rows[Period] = { Period };
        spoilDisposalChoices.forEach((m: string) => { table2Rows[Period][m] = 0; });
      }
      if (SpoilDisposalMethod != null) {
        const m = String(SpoilDisposalMethod);
        table2Rows[Period][m] = (table2Rows[Period][m] ?? 0) + Math.round(Number(QuantityTonne) || 0);
      }
    });

    const mergedData2 = uniquePeriod.map(Period => {
      const row = table2Rows[Period] ?? { Period };
      const periodItems = filteredData.filter((item: any) => item.Period === Period);
      const sumQty = Math.round(periodItems.reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0));
      const sumNotReused = Math.round(periodItems.filter((item: any) => item.Reused === 'Not Reused').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0));
      const sumReused = Math.round(periodItems.filter((item: any) => item.Reused === 'Reuse').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0));
      const methodTotal = spoilDisposalChoices.reduce((s: number, m: string) => s + (row[m] ?? 0), 0);
      return {
        Period,
        'Number Of Samples': periodItems.length,
        Total: sumQty,
        'Total Not Reused (Tonnes)': sumNotReused,
        'Total Used / Potemtial Reuse (Tonnes)': sumReused,
        ...spoilDisposalChoices.reduce((acc: any, m: string) => { acc[m] = row[m] ?? 0; return acc; }, {}),
        'Total (By Disposal Method)': methodTotal,
      };
    });
    const columnKeys2 = mergedData2.length > 0
      ? Object.keys(mergedData2[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    // --- KPI ---
    const totalQty = filteredData.reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);
    const totalReused = filteredData.filter((item: any) => item.Reused === 'Reuse').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);
    const totalNotReused = filteredData.filter((item: any) => item.Reused === 'Not Reused').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);

    return {
      bySpoilCategoryPieData,
      bySpoilDisposalMethodPieData,
      lineDataByType,
      lineDataByDisposal,
      mergedData1,
      columnKeys1,
      mergedData2,
      columnKeys2,
      totalQty,
      totalReused,
      totalNotReused,
    };
  }, [filteredData, spoilLogColumns]);

  const kpiItems = [
    { label: 'Total Qty (T)', value: Math.round(chartData.totalQty), color: '#00695C' },
    { label: 'Entries', value: filteredData.length, color: '#4527A0' },
    { label: 'Reused (T)', value: Math.round(chartData.totalReused), color: '#2E7D32' },
    { label: 'Not Reused (T)', value: Math.round(chartData.totalNotReused), color: '#F57C00' },
  ];

  const kpiRow = (
    <View style={{ zIndex: 200 }}>
      <LogKpiRow items={kpiItems}>
        <LogDateRangeFilter data={spoilLogData} onFilterChange={setFilteredData} />
      </LogKpiRow>
    </View>
  );

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              {kpiRow}
              <View>
                <View style={logStyles.container}>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                    <PieChartComp data={chartData.bySpoilCategoryPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Quantities/Type"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                    <LineChartScreen legend={chartData.lineDataByType.legend} data={chartData.lineDataByType} label={chartData.lineDataByType.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Quantities/Type"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.bySpoilDisposalMethodPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Qty /(Destination)"} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={chartData.lineDataByDisposal.legend} data={chartData.lineDataByDisposal} label={chartData.lineDataByDisposal.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Qty /Destination"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardTwo}>
                    {chartData.mergedData1 && chartData.columnKeys1 ? (
                      <CustomDT
                        data={chartData.mergedData1}
                        columns={chartData.columnKeys1}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'SpoilQuantities'}
                        tableTitle={"Spoil Quantities (tonne)"}
                      />
                    ) : null}
                  </View>
                  <View style={logStyles.cardTwo}>
                    {chartData.mergedData2 && chartData.columnKeys2 ? (
                      <CustomDT
                        data={chartData.mergedData2}
                        columns={chartData.columnKeys2}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'SpoilReuse'}
                        tableTitle={"Spoil Quantities (tonne) & Spoil Potential Reuse"}
                      />
                    ) : null}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={() => {}}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
